// Servicio de Mercado Pago para gestión de suscripciones
// Compatible con la estructura existente de la base de datos
// Basado en la documentación del Panel de Control
// Usa proxy local para evitar problemas de CORS

import { supabase } from '../lib/supabase';
import { ERROR_MESSAGES } from '../constants/subscriptions';
import axios from 'axios';

/**
 * Obtiene la URL base para callbacks de Mercado Pago
 * Usa ngrok en desarrollo si está configurado, sino usa el origin actual
 */
function getBaseUrl() {
  // En desarrollo, priorizar ngrok si está configurado
  if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_NGROK_URL) {
    return process.env.REACT_APP_NGROK_URL;
  }
  
  // En producción o sin ngrok, usar el origin actual
  return window.location.origin;
}

/**
 * URL base del servidor proxy local
 */
const PROXY_BASE_URL = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:3333' 
  : window.location.origin;

/**
 * Cliente HTTP para comunicarse con el proxy local
 */
const proxyClient = axios.create({
  baseURL: `${PROXY_BASE_URL}/api/mercadopago`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 segundos timeout
});

/**
 * Interceptor para manejo de errores del proxy
 */
proxyClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Mercado Pago Proxy Error:', error);
    
    // Mostrar error detallado del servidor
    if (error.response?.data) {
      console.error('Error detallado del servidor:', JSON.stringify(error.response.data, null, 2));
    }
    
    // Manejar errores específicos del proxy
    if (error.response?.status === 400) {
      error.message = 'Datos inválidos en la solicitud';
    } else if (error.response?.status === 401) {
      error.message = 'No autorizado - Verifica tus credenciales';
    } else if (error.response?.status === 403) {
      error.message = 'Acceso denegado';
    } else if (error.response?.status === 429) {
      error.message = 'Demasiadas solicitudes - Intenta más tarde';
    } else if (error.response?.status >= 500) {
      error.message = 'Error del servidor proxy';
    } else if (error.code === 'ECONNREFUSED') {
      error.message = 'Servidor proxy no disponible - Inicia el servidor con npm run server';
    }
    
    return Promise.reject(error);
  }
);

/**
 * Clase para gestionar suscripciones con Mercado Pago
 * Adaptada a la estructura existente de la base de datos
 * Usa proxy local para evitar problemas de CORS
 */
class MercadoPagoService {
  constructor() {
    this.baseURL = `${PROXY_BASE_URL}/api/mercadopago`;
    this.accessToken = null;
    this.publicKey = null;
  }

  /**
   * Inicializa el servicio con credenciales
   * @param {string} accessToken - Token de acceso de Mercado Pago
   * @param {string} publicKey - clave pública para frontend
   */
  initialize(accessToken, publicKey) {
    this.accessToken = accessToken;
    this.publicKey = publicKey;
  }

  /**
   * Obtiene el precio efectivo del plan (promocional si existe, sino el normal)
   * @param {Object} plan - Datos del plan
   * @returns {number} Precio efectivo a cobrar
   */
  getEffectivePrice(plan) {
    // Si existe precio promocional y es diferente de cero, usarlo
    if (plan.plan_price_promo !== null && plan.plan_price_promo !== undefined) {
      return plan.plan_price_promo;
    }
    // Sino usar el precio normal
    return plan.plan_price || plan.precio;
  }

  /**
   * Crea una preaprobación de suscripción en Mercado Pago
   * @param {Object} subscriptionData - Datos de la suscripción
   * @param {string} subscriptionData.planId - ID del plan
   * @param {string} subscriptionData.userId - ID del usuario
   * @param {string} subscriptionData.email - Email del usuario
   * @param {Object} plan - Datos del plan desde Supabase
   */
  async createSubscription(subscriptionData, plan) {
    try {
      if (!this.accessToken) {
        throw new Error('Servicio no inicializado. Falta access token.');
      }

      // Obtener precio efectivo (promocional si existe)
      const effectivePrice = this.getEffectivePrice(plan);

      // 1. Crear o reemplazar suscripción en Supabase usando la función RPC
      const { data: rpcResult, error: rpcError } = await supabase.rpc('replace_subscription', {
        p_usuario_id: subscriptionData.userId,
        p_plan_id: subscriptionData.planId,
        p_monto: effectivePrice,
        p_moneda: plan.moneda || 'ARS',
        p_frecuencia: 'monthly'
      });

      if (rpcError) throw rpcError;

      // Verificar si la operación fue exitosa
      if (!rpcResult || rpcResult.length === 0 || !rpcResult[0].success) {
        throw new Error(rpcResult?.[0]?.message || 'Error al crear suscripción');
      }

      console.log('✅ Suscripción creada/reemplazada:', rpcResult[0].message);

      // Obtener la suscripción creada
      const { data: subscription, error: subError } = await supabase
        .from('suscripciones')
        .select('*')
        .eq('id', rpcResult[0].subscription_id)
        .single();

      if (subError) throw subError;

      // 2. Crear preaprobación en Mercado Pago a través del proxy
      const preapprovalData = {
        reason: `Suscripción mensual - ${plan.nombre}`,
        auto_recurring: {
          frequency: 1,
          frequency_type: 'months',
          transaction_amount: effectivePrice, // Usar precio efectivo
          currency_id: plan.moneda || 'ARS', // Usar la moneda del plan
          start_date: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // Mañana en formato ISO completo
          end_date: null // Sin fecha de fin
        },
        back_url: `${getBaseUrl()}/mp-return`,
        payer_email: subscriptionData.email, // Corregido: payer_email en lugar de payer.email
        external_reference: subscription.id.toString(), // ID numérico como string
        status: 'pending'
      };

      // Mostrar datos que se enviarán para depuración
      console.log('Datos enviados a Mercado Pago:', JSON.stringify(preapprovalData, null, 2));
      console.log('Precio efectivo utilizado:', effectivePrice, '(promocional si existe)');

      const response = await proxyClient.post('/preapproval', preapprovalData);
      const mpData = response.data;

      // 3. Actualizar suscripción con ID de Mercado Pago
      const { error: updateError } = await supabase
        .from('suscripciones')
        .update({
          mp_preapproval_id: mpData.id,
          datos_pago: {
            init_point: mpData.init_point,
            sandbox_init_point: mpData.sandbox_init_point
          },
          actualizado_en: new Date().toISOString()
        })
        .eq('id', subscription.id);

      if (updateError) throw updateError;

      return {
        success: true,
        subscription,
        mercadoPagoData: mpData,
        initPoint: mpData.init_point || mpData.sandbox_init_point
      };

    } catch (error) {
      console.error('Error en createSubscription:', error);
      return {
        success: false,
        error: error.message || ERROR_MESSAGES.GENERIC_ERROR
      };
    }
  }

  /**
   * Cancela una suscripción en Mercado Pago y Supabase
   * @param {number} subscriptionId - ID numérico de la suscripción en Supabase
   * @param {string} mpPreapprovalId - ID de preaprobación en Mercado Pago
   */
  async cancelSubscription(subscriptionId, mpPreapprovalId) {
    try {
      if (!this.accessToken) {
        throw new Error('Servicio no inicializado. Falta access token.');
      }

      // 1. Cancelar en Mercado Pago a través del proxy
      const response = await proxyClient.put(`/preapproval/${mpPreapprovalId}`, {
        status: 'cancelled'
      });

      const mpData = response.data;

      // 2. Actualizar en Supabase
      const { error: updateError } = await supabase
        .from('suscripciones')
        .update({
          estado: 'cancelled',
          cancelado_en: new Date().toISOString(),
          motivo_cancelacion: 'Cancelado desde Mercado Pago'
        })
        .eq('id', subscriptionId);

      if (updateError) throw updateError;

      return {
        success: true,
        message: 'Suscripción cancelada exitosamente'
      };

    } catch (error) {
      console.error('Error en cancelSubscription:', error);
      return {
        success: false,
        error: error.message || ERROR_MESSAGES.GENERIC_ERROR
      };
    }
  }

  /**
   * Pausa una suscripción activa
   * @param {number} subscriptionId - ID numérico de la suscripción en Supabase
   * @param {string} mpPreapprovalId - ID de preaprobación en Mercado Pago
   */
  async pauseSubscription(subscriptionId, mpPreapprovalId) {
    try {
      if (!this.accessToken) {
        throw new Error('Servicio no inicializado. Falta access token.');
      }

      // 1. Pausar en Mercado Pago a través del proxy
      const response = await proxyClient.put(`/preapproval/${mpPreapprovalId}`, {
        status: 'paused'
      });

      const mpData = response.data;

      // 2. Actualizar en Supabase
      const { error: updateError } = await supabase
        .from('suscripciones')
        .update({
          estado: 'paused',
          actualizado_en: new Date().toISOString()
        })
        .eq('id', subscriptionId);

      if (updateError) throw updateError;

      return {
        success: true,
        message: 'Suscripción pausada exitosamente'
      };

    } catch (error) {
      console.error('Error en pauseSubscription:', error);
      return {
        success: false,
        error: error.message || ERROR_MESSAGES.GENERIC_ERROR
      };
    }
  }

  /**
   * Reanuda una suscripción pausada
   * @param {number} subscriptionId - ID numérico de la suscripción en Supabase
   * @param {string} mpPreapprovalId - ID de preaprobación en Mercado Pago
   */
  async resumeSubscription(subscriptionId, mpPreapprovalId) {
    try {
      if (!this.accessToken) {
        throw new Error('Servicio no inicializado. Falta access token.');
      }

      // 1. Reanudar en Mercado Pago a través del proxy
      const response = await proxyClient.put(`/preapproval/${mpPreapprovalId}`, {
        status: 'authorized'
      });

      const mpData = response.data;

      // 2. Actualizar en Supabase
      const { error: updateError } = await supabase
        .from('suscripciones')
        .update({
          estado: 'active',
          actualizado_en: new Date().toISOString()
        })
        .eq('id', subscriptionId);

      if (updateError) throw updateError;

      return {
        success: true,
        message: 'Suscripción reanudada exitosamente'
      };

    } catch (error) {
      console.error('Error en resumeSubscription:', error);
      return {
        success: false,
        error: error.message || ERROR_MESSAGES.GENERIC_ERROR
      };
    }
  }

  /**
   * Obtiene el estado de una suscripción desde Mercado Pago
   * @param {string} mpPreapprovalId - ID de preaprobación en Mercado Pago
   */
  async getSubscriptionStatus(mpPreapprovalId) {
    try {
      if (!this.accessToken) {
        throw new Error('Servicio no inicializado. Falta access token.');
      }

      const response = await proxyClient.get(`/preapproval/${mpPreapprovalId}`);
      const mpData = response.data;

      return {
        success: true,
        status: mpData.status,
        data: mpData
      };

    } catch (error) {
      console.error('Error en getSubscriptionStatus:', error);
      return {
        success: false,
        error: error.message || ERROR_MESSAGES.GENERIC_ERROR
      };
    }
  }

  /**
   * Sincroniza planes con Mercado Pago
   * Crea/actualiza planes en Mercado Pago basados en la base de datos local
   */
  async syncPlans() {
    try {
      if (!this.accessToken) {
        throw new Error('Servicio no inicializado. Falta access token.');
      }

      // Obtener planes locales activos
      const { data: localPlans, error: plansError } = await supabase
        .from('planes')
        .select('*')
        .eq('es_activo', true);

      if (plansError) throw plansError;

      const syncResults = [];

      for (const plan of localPlans) {
        try {
          // Verificar si ya existe en Mercado Pago
          if (plan.mp_code) {
            // Actualizar plan existente
            const updateData = {
              title: plan.plan_name || plan.nombre,
              description: plan.descripcion,
              auto_recurring: {
                frequency: 1,
                frequency_type: 'months',
                transaction_amount: this.getEffectivePrice(plan), // Usar precio efectivo
                currency_id: 'MXN'
              },
              status: 'active'
            };

            const response = await proxyClient.put(`/preapproval_plan/${plan.mp_code}`, updateData);
            
            if (response.data) {
              syncResults.push({ planId: plan.id, action: 'updated', success: true });
            } else {
              syncResults.push({ planId: plan.id, action: 'update_failed', success: false });
            }
          } else {
            // Crear nuevo plan en Mercado Pago
            const planData = {
              title: plan.plan_name || plan.nombre,
              description: plan.descripcion,
              auto_recurring: {
                frequency: 1,
                frequency_type: 'months',
                transaction_amount: this.getEffectivePrice(plan), // Usar precio efectivo
                currency_id: 'MXN'
              },
              back_url: `${getBaseUrl()}/mp-return`,
              status: 'active'
            };

            const response = await proxyClient.post('/preapproval_plan', planData);
            
            if (response.data) {
              const mpPlan = response.data;
              
              // Actualizar plan local con ID de Mercado Pago
              await supabase
                .from('planes')
                .update({ mp_code: mpPlan.id })
                .eq('id', plan.id);

              syncResults.push({ planId: plan.id, action: 'created', success: true, mpId: mpPlan.id });
            } else {
              syncResults.push({ planId: plan.id, action: 'create_failed', success: false });
            }
          }
        } catch (error) {
          syncResults.push({ planId: plan.id, action: 'error', success: false, error: error.message });
        }
      }

      return {
        success: true,
        results: syncResults
      };

    } catch (error) {
      console.error('Error en syncPlans:', error);
      return {
        success: false,
        error: error.message || ERROR_MESSAGES.GENERIC_ERROR
      };
    }
  }

  /**
   * Actualiza el estado de una suscripción localmente (fallback)
   * @param {string} preapprovalId - ID de preaprobación de Mercado Pago
   */
  async updateSubscriptionLocally(preapprovalId) {
    try {
      console.log('🔄 Actualizando suscripción localmente (fallback)...', { preapprovalId });

      // 1. Buscar la suscripción local por preapproval_id
      const { data: subscription, error: findError } = await supabase
        .from('suscripciones')
        .select('*')
        .eq('mp_preapproval_id', preapprovalId)
        .single();

      if (findError) {
        console.error('Error buscando suscripción local:', findError);
        throw new Error('Suscripción no encontrada en la base de datos local');
      }

      console.log('Suscripción encontrada:', subscription);

      // 2. Actualizar la suscripción a estado 'active' directamente
      const updateData = {
        estado: 'active',
        actualizado_en: new Date().toISOString(),
        datos_pago: {
          ...subscription.datos_pago,
          status: 'authorized',
          last_updated: new Date().toISOString(),
          updated_locally: true
        }
      };

      // Establecer fechas si no existen
      if (!subscription.fecha_inicio) {
        updateData.fecha_inicio = new Date().toISOString().split('T')[0];
      }
      if (!subscription.fecha_fin && subscription.frecuencia === 'monthly') {
        const fechaFinDate = new Date();
        fechaFinDate.setMonth(fechaFinDate.getMonth() + 1);
        updateData.fecha_fin = fechaFinDate.toISOString().split('T')[0];
      }

      const { error: updateError } = await supabase
        .from('suscripciones')
        .update(updateData)
        .eq('id', subscription.id);

      if (updateError) {
        console.error('Error actualizando suscripción:', updateError);
        throw new Error('Error al actualizar la suscripción en la base de datos');
      }

      // 3. Registrar el pago
      await this.recordPayment(subscription.id, {
        monto: subscription.monto,
        moneda: subscription.moneda || 'ARS',
        mp_status: 'authorized',
        datos_pago: { status: 'authorized', updated_locally: true },
        fecha_pago: new Date().toISOString()
      });

      console.log('✅ Suscripción actualizada localmente:', {
        subscriptionId: subscription.id,
        newEstado: 'active'
      });

      return {
        success: true,
        subscription: { ...subscription, ...updateData },
        mercadoPagoStatus: 'authorized',
        updatedLocally: true
      };

    } catch (error) {
      console.error('Error en updateSubscriptionLocally:', error);
      return {
        success: false,
        error: error.message || ERROR_MESSAGES.GENERIC_ERROR
      };
    }
  }

  /**
   * Actualiza el estado de una suscripción después del retorno exitoso
   * @param {string} preapprovalId - ID de preaprobación de Mercado Pago
   * @param {Object} subscriptionData - Datos adicionales de la suscripción
   */
  async updateSubscriptionAfterReturn(preapprovalId, subscriptionData = {}) {
    try {
      // Verificar si el servicio está inicializado
      if (!this.isInitialized()) {
        console.log('⚠️ Servicio no inicializado, usando fallback local...');
        return await this.updateSubscriptionLocally(preapprovalId);
      }

      // 1. Obtener estado actual desde Mercado Pago
      const mpStatusResponse = await this.getSubscriptionStatus(preapprovalId);
      
      if (!mpStatusResponse.success) {
        console.log('⚠️ No se pudo obtener estado de Mercado Pago, usando fallback local...');
        return await this.updateSubscriptionLocally(preapprovalId);
      }

      const mpData = mpStatusResponse.data;
      console.log('Estado desde Mercado Pago:', mpData);

      // 2. Buscar la suscripción local por preapproval_id
      const { data: subscription, error: findError } = await supabase
        .from('suscripciones')
        .select('*')
        .eq('mp_preapproval_id', preapprovalId)
        .single();

      if (findError) {
        console.error('Error buscando suscripción local:', findError);
        throw new Error('Suscripción no encontrada en la base de datos local');
      }

      console.log('Suscripción encontrada:', subscription);

      // 3. Determinar el nuevo estado basado en el estado de Mercado Pago
      let newEstado = 'pending';
      let fechaInicio = subscription.fecha_inicio;
      let fechaFin = subscription.fecha_fin;

      if (mpData.status === 'authorized' || mpData.status === 'active') {
        newEstado = 'active';
        // Si no tiene fecha de inicio, establecerla hoy
        if (!fechaInicio) {
          fechaInicio = new Date().toISOString().split('T')[0];
        }
        // Calcular fecha de fin según la frecuencia
        if (!fechaFin && subscription.frecuencia === 'monthly') {
          const fechaFinDate = new Date();
          fechaFinDate.setMonth(fechaFinDate.getMonth() + 1);
          fechaFin = fechaFinDate.toISOString().split('T')[0];
        }
      } else if (mpData.status === 'pending') {
        newEstado = 'pending';
      } else if (mpData.status === 'cancelled') {
        newEstado = 'cancelled';
      }

      // 4. Actualizar la suscripción en la base de datos
      const updateData = {
        estado: newEstado,
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin,
        actualizado_en: new Date().toISOString(),
        datos_pago: {
          ...subscription.datos_pago,
          ...mpData,
          last_updated: new Date().toISOString()
        }
      };

      // Agregar información adicional si está disponible
      if (subscriptionData.payer) {
        updateData.metodo_pago = {
          payer: subscriptionData.payer,
          payment_method_id: subscriptionData.payment_method_id
        };
      }

      const { error: updateError } = await supabase
        .from('suscripciones')
        .update(updateData)
        .eq('id', subscription.id);

      if (updateError) {
        console.error('Error actualizando suscripción:', updateError);
        throw new Error('Error al actualizar la suscripción en la base de datos');
      }

      // 5. Si hay un pago aprobado, registrarlo
      if (mpData.status === 'authorized' || mpData.status === 'active') {
        await this.recordPayment(subscription.id, {
          monto: subscription.monto,
          moneda: subscription.moneda,
          mp_status: mpData.status,
          datos_pago: mpData,
          fecha_pago: new Date().toISOString()
        });
      }

      console.log('✅ Suscripción actualizada exitosamente:', {
        subscriptionId: subscription.id,
        newEstado,
        mpStatus: mpData.status
      });

      return {
        success: true,
        subscription: { ...subscription, ...updateData },
        mercadoPagoStatus: mpData.status
      };

    } catch (error) {
      console.error('Error en updateSubscriptionAfterReturn:', error);
      return {
        success: false,
        error: error.message || ERROR_MESSAGES.GENERIC_ERROR
      };
    }
  }

  /**
   * Registra un pago de suscripción
   * @param {number} subscriptionId - ID de la suscripción
   * @param {Object} paymentData - Datos del pago
   */
  async recordPayment(subscriptionId, paymentData) {
    try {
      const { error: paymentError } = await supabase
        .from('pagos_suscripciones')
        .insert({
          suscripcion_id: subscriptionId,
          monto: paymentData.monto,
          moneda: paymentData.moneda || 'ARS',
          mp_status: paymentData.mp_status || 'pending',
          datos_pago: paymentData.datos_pago,
          fecha_pago: paymentData.fecha_pago,
          creado_en: new Date().toISOString()
        });

      if (paymentError) {
        console.error('Error registrando pago:', paymentError);
        // No lanzar error, solo loguear para no interrumpir el flujo
      }

      return { success: true };
    } catch (error) {
      console.error('Error en recordPayment:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Obtiene la clave pública para el frontend
   */
  getPublicKey() {
    return this.publicKey;
  }

  /**
   * Obtiene la URL de pago para una suscripción pendiente
   * @param {string} mpPreapprovalId - ID de preaprobación en Mercado Pago
   */
  async getPaymentUrl(mpPreapprovalId) {
    try {
      if (!this.accessToken) {
        throw new Error('Servicio no inicializado. Falta access token.');
      }

      const response = await proxyClient.get(`/preapproval/${mpPreapprovalId}`);
      const mpData = response.data;

      // Devolver la URL de inicio de pago según el entorno
      const paymentUrl = mpData.init_point || mpData.sandbox_init_point;

      return {
        success: true,
        paymentUrl,
        status: mpData.status,
        data: mpData
      };

    } catch (error) {
      console.error('Error en getPaymentUrl:', error);
      return {
        success: false,
        error: error.message || ERROR_MESSAGES.GENERIC_ERROR
      };
    }
  }

  /**
   * Reintenta el pago de una suscripción pendiente o fallida
   * @param {number} subscriptionId - ID numérico de la suscripción en Supabase
   * @param {string} mpPreapprovalId - ID de preaprobación en Mercado Pago
   */
  async retryPayment(subscriptionId, mpPreapprovalId) {
    try {
      if (!this.accessToken) {
        throw new Error('Servicio no inicializado. Falta access token.');
      }

      // 1. Obtener estado actual de la suscripción en Mercado Pago
      const statusResponse = await this.getSubscriptionStatus(mpPreapprovalId);
      
      if (!statusResponse.success) {
        throw new Error('No se pudo verificar el estado de la suscripción');
      }

      const mpData = statusResponse.data;

      // 2. Si la suscripción está cancelada o expirada, no se puede reintentar
      if (mpData.status === 'cancelled' || mpData.status === 'expired') {
        throw new Error('La suscripción está cancelada o expirada. Debes crear una nueva.');
      }

      // 3. Obtener la URL de pago para que el usuario pueda completarla
      const paymentResponse = await this.getPaymentUrl(mpPreapprovalId);
      
      if (!paymentResponse.success) {
        throw new Error('No se pudo obtener la URL de pago');
      }

      // 4. Actualizar el contador de intentos de pago en la base de datos
      const { data: currentSub, error: fetchError } = await supabase
        .from('suscripciones')
        .select('intentos_pago')
        .eq('id', subscriptionId)
        .single();

      if (!fetchError && currentSub) {
        const { error: updateError } = await supabase
          .from('suscripciones')
          .update({
            intentos_pago: (currentSub.intentos_pago || 0) + 1,
            actualizado_en: new Date().toISOString()
          })
          .eq('id', subscriptionId);

        if (updateError) {
          console.error('Error actualizando intentos de pago:', updateError);
          // No lanzar error, solo loguear para no interrumpir el flujo
        }
      }

      return {
        success: true,
        paymentUrl: paymentResponse.paymentUrl,
        message: 'Redirigiendo a Mercado Pago para completar el pago...',
        status: mpData.status
      };

    } catch (error) {
      console.error('Error en retryPayment:', error);
      return {
        success: false,
        error: error.message || ERROR_MESSAGES.GENERIC_ERROR
      };
    }
  }

  /**
   * Continúa con el pago de una suscripción pendiente
   * @param {number} subscriptionId - ID numérico de la suscripción en Supabase
   * @param {string} mpPreapprovalId - ID de preaprobación en Mercado Pago
   */
  async continuePayment(subscriptionId, mpPreapprovalId) {
    try {
      if (!this.accessToken) {
        throw new Error('Servicio no inicializado. Falta access token.');
      }

      // 1. Verificar que la suscripción esté en estado pendiente
      const { data: subscription, error: findError } = await supabase
        .from('suscripciones')
        .select('*')
        .eq('id', subscriptionId)
        .single();

      if (findError) {
        throw new Error('Suscripción no encontrada');
      }

      if (subscription.estado !== 'pending') {
        throw new Error('La suscripción no está en estado pendiente');
      }

      // 2. Obtener la URL de pago
      const paymentResponse = await this.getPaymentUrl(mpPreapprovalId);
      
      if (!paymentResponse.success) {
        throw new Error('No se pudo obtener la URL de pago');
      }

      return {
        success: true,
        paymentUrl: paymentResponse.paymentUrl,
        message: 'Redirigiendo a Mercado Pago para continuar con el pago...',
        status: paymentResponse.status
      };

    } catch (error) {
      console.error('Error en continuePayment:', error);
      return {
        success: false,
        error: error.message || ERROR_MESSAGES.GENERIC_ERROR
      };
    }
  }

  /**
   * Verifica si el servicio está inicializado
   */
  isInitialized() {
    return !!(this.accessToken && this.publicKey);
  }
}

// Exportar instancia única
const mercadoPagoService = new MercadoPagoService();

export default mercadoPagoService;
